import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ApiClient from "../../config/AppAdmin";
import { toast } from "react-toastify";

export const fetchAdmins = createAsyncThunk("admins/fetchAdmins", async () => {
  try {
    const response = await ApiClient.get("admin");
    if (
      !Array.isArray(response.data.data) ||
      !Array.isArray(response.data.data[0])
    ) {
      throw new Error("Invalid data structure: Expected nested arrays");
    }
    return response.data.data[0];
  } catch (error) {
    console.error("Error fetching admins:", error);
    throw error;
  }
});

export const updateAdmin = createAsyncThunk(
  "admins/updateAdmin",
  async ({
    aid,
    admin_name,
    admin_contact,
    phone,
    address,
    role,
    salary,
    salary_details,
  }) => {
    try {
      const response = await ApiClient.patch(`admin/${aid}`, {
        admin_name,
        admin_contact,
        phone,
        address,
        role,
        salary,
        salary_details,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating admin:", error);
      throw error;
    }
  },
);

export const deleteAdmin = createAsyncThunk(
  "admins/deleteAdmin",
  async (aid) => {
    try {
      const response = await ApiClient.delete(`admin/${aid}`);
      if (!response.data.status === "success") {
        throw new Error(response.data.message || "Delete failed");
      }
      return aid;
    } catch (error) {
      console.error("Error deleting admin:", error);
      throw error;
    }
  },
);

// ✅ NEW: Add salary detail
export const addSalaryDetail = createAsyncThunk(
  "admins/addSalaryDetail",
  async ({ aid, salaryDetail }) => {
    try {
      const response = await ApiClient.patch(
        `admin/salary_details/${aid}`,
        salaryDetail,
      );
      return { aid, salaryDetail, data: response.data };
    } catch (error) {
      console.error("Error adding salary detail:", error);
      throw error;
    }
  },
);
// delete salary details
export const deleteSalaryDetail = createAsyncThunk(
  "admins/deleteSalaryDetail",
  async ({ aid, full_date }) => {
    try {
      const response = await ApiClient.delete(`admin/salary_details/${aid}`, {
        data: { full_date }, // axios requires body in `data` for DELETE
      });
      return { aid, full_date, data: response.data };
    } catch (error) {
      console.error("Error deleting salary detail:", error);
      throw error;
    }
  },
);
const adminSlice = createSlice({
  name: "staff",
  initialState: {
    admins: [],
    loading: false,
    error: null,
    salaryLoading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchAdmins.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdmins.fulfilled, (state, action) => {
        state.loading = false;
        state.admins = action.payload;
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Update
      .addCase(updateAdmin.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAdmin.fulfilled, (state, action) => {
        state.loading = false;
        const updatedAdmin = action.payload;
        const index = state.admins.findIndex((a) => a.aid === updatedAdmin.aid);
        if (index !== -1) state.admins[index] = updatedAdmin;
        toast.success("Updated successfully");
      })
      .addCase(updateAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error("Failed to update: " + action.error.message);
      })

      // Delete
      .addCase(deleteAdmin.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.admins = state.admins.filter(
          (admin) => admin.aid !== action.payload,
        );
        toast.success("Admin deleted successfully");
      })
      .addCase(deleteAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error("Failed to delete: " + action.error.message);
      })

      // ✅ Add Salary Detail
      .addCase(addSalaryDetail.pending, (state) => {
        state.salaryLoading = true;
      })
      .addCase(addSalaryDetail.fulfilled, (state, action) => {
        state.salaryLoading = false;
        const { aid, salaryDetail } = action.payload;
        const index = state.admins.findIndex((a) => a.aid === parseInt(aid));
        if (index !== -1) {
          const existing = state.admins[index];
          let details = [];
          try {
            details = Array.isArray(existing.salary_details)
              ? existing.salary_details
              : JSON.parse(existing.salary_details || "[]");
          } catch {
            details = [];
          }
          state.admins[index] = {
            ...existing,
            salary_details: [...details, salaryDetail],
          };
        }
        toast.success("Salary detail added successfully");
      })
      .addCase(addSalaryDetail.rejected, (state, action) => {
        state.salaryLoading = false;
        toast.error("Failed to add salary detail: " + action.error.message);
      })
      // Delete Salary Detail
      .addCase(deleteSalaryDetail.pending, (state) => {
        state.salaryLoading = true;
      })
      .addCase(deleteSalaryDetail.fulfilled, (state, action) => {
        state.salaryLoading = false;
        const { aid, full_date } = action.payload;
        const index = state.admins.findIndex((a) => a.aid === parseInt(aid));
        if (index !== -1) {
          const existing = state.admins[index];
          let details = [];
          try {
            details = Array.isArray(existing.salary_details)
              ? existing.salary_details
              : JSON.parse(existing.salary_details || "[]");
          } catch {
            details = [];
          }

          state.admins[index] = {
            ...existing,
            salary_details: details.filter((d) => d.full_date !== full_date),
          };
        }
        toast.success("Salary detail deleted successfully");
      })
      .addCase(deleteSalaryDetail.rejected, (state, action) => {
        state.salaryLoading = false;
        toast.error("Failed to delete salary detail: " + action.error.message);
      });
  },
});

export default adminSlice.reducer;
